import { createContext, useContext } from "react";
import {
  ChatMessage,
  AgentChatUser,
  ConversationState,
} from "@/components/agent/chat/types";
import { Tables } from "@/types/database.types";

// Define the context type and export it
export type ChatContextType = {
  agent: Tables<"agents">;
  organization?: Tables<"organizations">;
  isPublic: boolean;
  chatMessages: ChatMessage[];
  isLoading: boolean;
  isConversationLoading: boolean;
  conversationId: string | null;
  conversationState: ConversationState;
  agentChatUser: AgentChatUser | null;
  previousResponseId: string | null;
  isFirstInteraction: boolean;
  canResetConversation: boolean;
  handleSendMessage: (messageText: string) => void;
  handleResetConversation: () => void;
  handleCloseConversation: () => void;
  handleReopenConversation: () => void;
  brandColor: string;
  textColor: string;
  debugMode: boolean;
  setChatMessages: (messages: ChatMessage[]) => void;
  setConversationId: (id: string | null) => void;
  setConversationState: (state: ConversationState) => void;
  setIsFirstInteraction: (value: boolean) => void;
  // Calendly metadata for tool outputs
  calendlyMeta?: {
    tool_call_id: string;
    response_id: string;
    button_text: string;
  } | null;
  setCalendlyMeta?: (
    meta: {
      tool_call_id: string;
      response_id: string;
      button_text: string;
    } | null
  ) => void;
  // New properties for pagination
  hasMoreMessages?: boolean;
  loadMoreMessages?: () => Promise<void>;
  isLoadingMore?: boolean;
};

// Create the context
export const ChatContext = createContext<ChatContextType | undefined>(
  undefined
);

// Export a hook to use the context
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
